import {
  arbitrum,
  base,
  bsc,
  celo,
  ethereum,
  linea,
  optimism,
  polygon,
  scroll,
  solana,
  worldchain,
} from "@daimo/pay-common";

type Logo = {
  testnet?: boolean;
};

const KnownChain = ({ testnet, ...props }: Logo) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "black",
    }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.5611 8.12948C21.0082 7.90729 21.5007 7.79167 22 7.79167C22.4993 7.79167 22.9919 7.90729 23.439 8.12948L23.4408 8.1304L33.0387 12.9293C33.577 13.197 34.031 13.61 34.3478 14.121C34.6649 14.6323 34.833 15.2218 34.8333 15.8234V27.2595C34.833 27.8611 34.6649 28.4511 34.3478 28.9624C34.031 29.4733 33.578 29.8858 33.0398 30.1535L23.4411 34.9528C22.9919 35.1775 22.4963 35.2947 21.994 35.2947C21.4918 35.2947 20.9964 35.1777 20.5472 34.9529L10.9475 30.1531L10.9452 30.1519C10.4071 29.8808 9.95535 29.4646 9.6411 28.9504C9.32739 28.437 9.16312 27.8464 9.16673 27.2448L9.16675 27.2417L10.0004 27.2475H9.16673V27.2448V15.8239C9.16705 15.2223 9.33518 14.6322 9.65222 14.121C9.96906 13.61 10.4221 13.1976 10.9604 12.9298L20.5592 8.1304L20.5611 8.12948ZM21.3031 9.62267L11.8706 14.3389L22 19.4036L32.1294 14.3389L22.697 9.62267C22.4806 9.51531 22.2416 9.45905 22 9.45905C21.7585 9.45905 21.5194 9.51534 21.3031 9.62267ZM10.8341 15.8241C10.8341 15.7785 10.8362 15.733 10.8401 15.6878L21.1663 20.8509V33.3983L11.6955 28.6629C11.4352 28.5315 11.2159 28.3297 11.0638 28.0809C10.9116 27.8318 10.8321 27.5452 10.8341 27.2533L10.8341 27.2475V15.8241ZM22.8337 33.3923L32.2967 28.6608C32.5576 28.5312 32.7772 28.3313 32.9308 28.0836C33.0844 27.836 33.1658 27.5504 33.166 27.259V15.8243C33.1659 15.7786 33.1639 15.7331 33.1599 15.6878L22.8337 20.8509V33.3923Z"
      fill="url(#paint0_linear_3546_7073)"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.8341 15.8241C10.8341 15.7785 10.8362 15.733 10.8401 15.6878L21.1663 20.8509V33.3983L11.6955 28.6629C11.4352 28.5315 11.2159 28.3297 11.0638 28.0809C10.9116 27.8318 10.8321 27.5452 10.8341 27.2533L10.8341 27.2475V15.8241Z"
      fill="url(#paint1_linear_3546_7073)"
      fillOpacity="0.3"
    />
    <defs>
      <linearGradient
        id="paint0_linear_3546_7073"
        x1="22"
        y1="7.79167"
        x2="22"
        y2="35.2947"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0.7" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_3546_7073"
        x1="22"
        y1="7.79167"
        x2="22"
        y2="35.2947"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0.7" />
      </linearGradient>
    </defs>
  </svg>
);

export const UnknownChain = ({ testnet, ...props }: Logo) => {
  return <KnownChain testnet {...props} />;
};

export const Ethereum = ({ testnet, ...props }: Logo) => {
  let bg = "var(--ck-chain-ethereum-01, #25292E)";
  let fill = "var(--ck-chain-ethereum-02, #ffffff)";

  if (testnet) {
    bg = "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)";
    fill = "#fff";
  }

  return (
    <svg
      {...props}
      aria-hidden="true"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        background: bg,
      }}
    >
      <path
        d="M21.9967 6.99621L21.7955 7.67987V27.5163L21.9967 27.7171L31.2044 22.2744L21.9967 6.99621Z"
        fill={fill}
      />
      <path
        d="M21.9957 6.99621L12.7878 22.2744L21.9957 27.7171V18.0891V6.99621Z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.9959 36.9996L21.9959 36.9997V36.9995L31.2091 24.0243L21.9959 29.4642L12.788 24.0243L21.9957 36.9993L21.9958 36.9997L21.9959 36.9996Z"
        fill={fill}
      />
      <path
        d="M21.996 27.7181L31.2037 22.2753L21.996 18.09V27.7181Z"
        fill={fill}
      />
      <path
        d="M12.7878 22.2753L21.9957 27.7181V18.09L12.7878 22.2753Z"
        fill={fill}
      />
    </svg>
  );
};

export const Polygon = ({ testnet, ...props }: Logo) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#6F41D8",
    }}
  >
    <path
      d="M29.0015 17.4529C28.4941 17.1572 27.8355 17.1572 27.2773 17.4529L23.3186 19.7271L20.6305 21.2094L16.6719 23.4822C16.1645 23.7792 15.5059 23.7792 14.9476 23.4822L11.8016 21.703C11.2943 21.4074 10.9395 20.8642 10.9395 20.2702V16.7612C10.9395 16.1686 11.2434 15.6255 11.8016 15.3285L14.8954 13.5988C15.4041 13.3018 16.0641 13.3018 16.6224 13.5988L19.7161 15.3285C20.2249 15.6255 20.5796 16.1686 20.5796 16.7612V19.0355L23.2678 17.5024V15.2295C23.2707 14.9343 23.1917 14.6441 23.0395 14.3911C22.8873 14.1381 22.6679 13.9324 22.4056 13.7968L16.6719 10.5353C16.1645 10.2382 15.5059 10.2382 14.9476 10.5353L9.11214 13.7968C8.84992 13.9324 8.63049 14.1381 8.47828 14.3911C8.32607 14.6441 8.24705 14.9343 8.25002 15.2295V21.802C8.25002 22.396 8.55389 22.9391 9.11214 23.2361L14.9476 26.4976C15.455 26.7932 16.115 26.7932 16.6719 26.4976L20.6305 24.2729L23.3186 22.7411L27.2773 20.5177C27.7846 20.2207 28.4433 20.2207 29.0015 20.5177L32.0966 22.2475C32.6054 22.5431 32.9588 23.0863 32.9588 23.6803V27.1893C32.9588 27.7819 32.6563 28.325 32.0966 28.622L29.0029 30.4013C28.4941 30.6983 27.8341 30.6983 27.2773 30.4013L24.1821 28.6715C23.6734 28.3745 23.3186 27.8314 23.3186 27.2387V24.9645L20.6305 26.4976V28.7705C20.6305 29.3631 20.9344 29.9076 21.4926 30.2032L27.3281 33.4647C27.8355 33.7617 28.4941 33.7617 29.0524 33.4647L34.8879 30.2032C35.3953 29.9076 35.75 29.3645 35.75 28.7705V22.198C35.753 21.9028 35.674 21.6126 35.5218 21.3596C35.3695 21.1066 35.1501 20.9009 34.8879 20.7653L29.0029 17.4529H29.0015Z"
      fill="white"
    />
  </svg>
);
export const Optimism = ({ testnet, ...props }: Logo) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#FF0420",
    }}
  >
    <path
      d="M15.5877 27.8473C14.2777 27.8473 13.2045 27.539 12.3679 26.9226C11.5422 26.2952 11.1294 25.4035 11.1294 24.2477C11.1294 24.0055 11.157 23.7082 11.212 23.356C11.3552 22.5634 11.5588 21.6112 11.823 20.4994C12.5715 17.4722 14.5034 15.9586 17.6187 15.9586C18.4664 15.9586 19.2259 16.1017 19.8974 16.3879C20.5689 16.663 21.0973 17.0814 21.4826 17.6428C21.8678 18.1932 22.0605 18.8537 22.0605 19.6242C22.0605 19.8554 22.033 20.1471 21.9779 20.4994C21.8128 21.4791 21.6146 22.4313 21.3835 23.356C20.9982 24.8641 20.3322 25.9924 19.3855 26.741C18.4388 27.4785 17.1729 27.8473 15.5877 27.8473ZM15.8189 25.4695C16.4354 25.4695 16.9582 25.2879 17.3876 24.9247C17.8279 24.5614 18.1416 24.0055 18.3287 23.257C18.5819 22.2222 18.7746 21.3195 18.9067 20.5489C18.9507 20.3178 18.9727 20.0811 18.9727 19.8389C18.9727 18.8372 18.4498 18.3363 17.4041 18.3363C16.7876 18.3363 16.2592 18.5179 15.8189 18.8812C15.3896 19.2445 15.0813 19.8004 14.8943 20.5489C14.6961 21.2865 14.4979 22.1892 14.2998 23.257C14.2557 23.477 14.2337 23.7082 14.2337 23.9504C14.2337 24.9632 14.7622 25.4695 15.8189 25.4695Z"
      fill="white"
    />
    <path
      d="M22.8188 27.6815C22.6977 27.6815 22.6041 27.6429 22.5381 27.5659C22.483 27.4778 22.4665 27.3788 22.4885 27.2687L24.7672 16.5358C24.7892 16.4147 24.8498 16.3156 24.9489 16.2385C25.0479 16.1615 25.1525 16.1229 25.2626 16.1229H29.6548C30.8767 16.1229 31.8564 16.3761 32.5939 16.8825C33.3426 17.3889 33.7168 18.1209 33.7168 19.0786C33.7168 19.3538 33.6838 19.64 33.6177 19.9372C33.3426 21.2032 32.7867 22.1389 31.95 22.7443C31.1244 23.3498 29.9905 23.6525 28.5485 23.6525H26.3194L25.5598 27.2687C25.5377 27.3898 25.4772 27.4888 25.3782 27.5659C25.2791 27.6429 25.1745 27.6815 25.0645 27.6815H22.8188ZM28.6641 21.3738C29.1264 21.3738 29.5282 21.2472 29.8695 20.994C30.2217 20.7408 30.4529 20.3776 30.563 19.9042C30.596 19.717 30.6125 19.552 30.6125 19.4089C30.6125 19.0896 30.519 18.8474 30.3318 18.6823C30.1446 18.5062 29.8255 18.4182 29.3741 18.4182H27.3926L26.7652 21.3738H28.6641Z"
      fill="white"
    />
  </svg>
);

export const Arbitrum = ({ testnet, ...props }: Logo) => {
  const fill = testnet ? "#ffffff" : "#28A0F0";
  const outlineFill = testnet ? "#ffffff" : "#96BEDC";
  return (
    <svg
      {...props}
      aria-hidden="true"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        background: testnet
          ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
          : "#2C364F",
      }}
    >
      {!testnet && (
        <path
          d="M25.7948 20.5826L28.2683 16.3854L34.9355 26.7696L34.9386 28.7625L34.9168 15.0491C34.9011 14.7137 34.7231 14.407 34.4391 14.2261L22.4357 7.32182C22.1551 7.1838 21.7989 7.18546 21.5187 7.32618C21.4807 7.34524 21.4453 7.36576 21.4113 7.38835L21.3694 7.41467L9.71816 14.1664L9.67298 14.1871C9.61474 14.2137 9.55609 14.2479 9.50076 14.2872C9.27983 14.4456 9.1331 14.68 9.08564 14.9425C9.07859 14.9823 9.0732 15.023 9.07092 15.064L9.08916 26.239L15.2994 16.6138C16.0811 15.3376 17.7847 14.9262 19.3662 14.9488L21.2221 14.9977L10.2862 32.5356L11.5753 33.2778L22.6422 15.0155L27.5338 14.9977L16.4956 33.7209L21.0955 36.3668L21.6451 36.6827C21.8776 36.7772 22.1516 36.7819 22.386 36.6972L34.5581 29.6433L32.2309 30.9918L25.7948 20.5826ZM26.7384 34.175L22.0925 26.8829L24.9287 22.0702L31.0303 31.6876L26.7384 34.175Z"
          fill={"#2D374B"}
        />
      )}
      <path
        d="M22.0924 26.8832L26.7385 34.1751L31.0302 31.6879L24.9286 22.0705L22.0924 26.8832Z"
        fill={fill}
      />
      <path
        d="M34.9387 28.7627L34.9356 26.7698L28.2684 16.3856L25.7949 20.5828L32.2312 30.992L34.5584 29.6435C34.7866 29.4582 34.9248 29.1861 34.9393 28.8926L34.9387 28.7627Z"
        fill={fill}
      />
      <path
        d="M7 30.642L10.2863 32.5356L21.2222 14.9976L19.3663 14.9487C17.785 14.9263 16.0814 15.3375 15.2995 16.6137L9.08927 26.239L7 29.449V30.642V30.642Z"
        fill="white"
      />
      <path
        d="M27.534 14.9977L22.6423 15.0155L11.5754 33.2778L15.4437 35.5049L16.4955 33.7209L27.534 14.9977Z"
        fill="white"
      />
      <path
        d="M37 14.9723C36.9592 13.9493 36.4052 13.013 35.5377 12.4677L23.377 5.47434C22.5187 5.04223 21.4466 5.04161 20.5868 5.47414C20.4852 5.52533 8.76078 12.3251 8.76078 12.3251C8.5985 12.4029 8.44224 12.4955 8.2953 12.6008C7.52081 13.156 7.0487 14.0186 7 14.9661V29.4492L9.08927 26.2392L9.07103 15.0639C9.07352 15.0231 9.0787 14.9827 9.08575 14.9431C9.133 14.6801 9.27994 14.4457 9.50086 14.2872C9.5562 14.2478 21.4806 7.34517 21.5186 7.32611C21.799 7.18538 22.155 7.18373 22.4356 7.32175L34.439 14.226C34.723 14.4069 34.901 14.7137 34.9167 15.049V28.8921C34.9022 29.1856 34.7862 29.4577 34.558 29.643L32.2308 30.9916L31.03 31.6875L26.7383 34.1747L22.3859 36.6969C22.1515 36.7817 21.8773 36.7769 21.645 36.6824L16.4955 33.7206L15.4435 35.5046L20.0713 38.169C20.2243 38.256 20.3607 38.3331 20.4726 38.3961C20.6458 38.4933 20.764 38.5582 20.8056 38.5785C21.1345 38.7383 21.6077 38.8311 22.0342 38.8311C22.4251 38.8311 22.8064 38.7594 23.1672 38.6181L35.8092 31.2971C36.5347 30.7348 36.9617 29.8869 37 28.9686V14.9723Z"
        fill={outlineFill}
      />
    </svg>
  );
};

export const Telos = ({ testnet, ...props }: Logo) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#571AFF",
    }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.1834 8.14754C25.6606 8.23504 25.3644 8.50235 24.9216 9.28591C24.5651 9.91722 24.4762 10.0244 24.2024 10.1592L23.9832 10.2668L19.2967 10.286L14.6097 10.3057L14.3875 10.3902C13.7059 10.6492 13.6192 10.7135 11.6291 12.4407C9.72243 14.0953 9.64893 14.1723 9.59249 14.5836C9.54437 14.9362 9.78981 15.6327 10.5191 17.2143C11.2847 18.8737 11.2839 18.8641 10.7444 19.5256C10.2645 20.1136 10.2269 20.2588 10.2041 21.5915C10.1717 23.502 10.2487 27.6023 10.3222 27.8591C10.3572 27.9816 10.7908 29.204 11.2861 30.5755C11.7813 31.9471 12.4192 33.715 12.704 34.5038C13.4281 36.5107 13.4814 36.5986 14.0392 36.7237C14.3066 36.7837 14.3206 36.781 18.9677 35.7258C24.4395 34.4837 23.7264 34.709 25.0739 33.7968C29.8732 30.5475 29.7337 30.66 29.8969 29.9083C30.0583 29.1642 30.1082 29.1379 31.8267 28.8999C34.6122 28.5145 34.6328 28.5083 34.8831 28.0109C35.0182 27.7423 35.7786 23.3406 35.8136 22.6209C35.8504 21.8828 36.042 22.221 33.3816 18.3395C30.022 13.4382 30.2381 13.7777 30.2399 13.4041C30.2407 13.1735 30.3366 12.9736 31.3236 11.1418C31.8236 10.2134 32.2742 9.35241 32.3254 9.22904C32.5236 8.74691 32.4204 8.3921 32.0301 8.21622L31.8267 8.12391L29.1102 8.11822C27.6048 8.11516 26.2997 8.12829 26.1834 8.14754ZM30.0474 9.4876C30.5623 9.72297 30.5382 9.82447 29.5119 11.7398C28.4317 13.7558 28.3157 13.2711 30.7154 16.7707C31.639 18.1173 32.8076 19.8218 33.3124 20.5581C34.6844 22.5592 34.6048 22.1799 34.1831 24.6903C33.7858 27.0602 33.7792 27.0817 33.3759 27.282C33.1506 27.394 33.2276 27.3813 30.8493 27.7117C28.9147 27.9803 28.8543 28.017 28.6719 29.0338C28.5778 29.557 28.4606 29.8169 28.2243 30.0247C28.0808 30.1512 24.8682 32.368 23.9451 32.9778C23.2587 33.4311 23.6861 33.3152 17.7471 34.6574C17.1997 34.7812 16.4079 34.9632 15.987 35.0617C14.4588 35.4195 14.4299 35.4033 13.8804 33.8948C12.9188 31.2528 11.6811 27.7957 11.6194 27.5787C11.5534 27.3463 11.549 27.1202 11.549 24.059V20.7878L11.6501 20.5966C11.7056 20.4912 11.8671 20.2759 12.0088 20.118C12.8418 19.19 12.8383 19.1183 11.8601 16.9907C10.7663 14.612 10.6797 14.9992 12.697 13.2501C14.2418 11.91 14.3048 11.8593 14.5905 11.7237L14.8394 11.6055L19.6983 11.5854C23.5417 11.5692 24.5891 11.5543 24.7103 11.515C25.1465 11.3728 25.4086 11.1094 25.7975 10.4203C26.3851 9.38041 26.3111 9.40797 28.4597 9.41891C29.6996 9.42547 29.9332 9.43554 30.0474 9.4876Z"
      fill="#F7F5FC"
    />
  </svg>
);

const Aurora = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#6CD544",
    }}
  >
    <path
      d="M22.0006 7.292C22.6198 7.29004 23.2271 7.46144 23.754 7.7868C24.2808 8.11216 24.706 8.57848 24.9816 9.133L34.3566 27.883C34.611 28.3912 34.7312 28.956 34.7058 29.5238C34.6805 30.0915 34.5103 30.6433 34.2116 31.1268C33.9129 31.6103 33.4956 32.0094 32.9992 32.2861C32.5028 32.5629 31.9439 32.7081 31.3756 32.708H12.6256C12.0573 32.7079 11.4985 32.5626 11.0023 32.2858C10.506 32.009 10.0888 31.6099 9.79022 31.1264C9.49163 30.6429 9.3216 30.0912 9.29628 29.5235C9.27096 28.9558 9.39119 28.3911 9.64556 27.883L19.0196 9.133C19.2951 8.57848 19.7203 8.11216 20.2472 7.7868C20.774 7.46144 21.3814 7.29004 22.0006 7.292ZM22.0006 5C20.9561 4.9999 19.9322 5.29059 19.0437 5.83952C18.1551 6.38846 17.4369 7.17394 16.9696 8.108L7.59456 26.858C7.16544 27.7156 6.96271 28.6687 7.00564 29.6268C7.04856 30.5848 7.33572 31.516 7.83982 32.3318C8.34392 33.1476 9.04823 33.821 9.88584 34.288C10.7235 34.755 11.6666 35.0001 12.6256 35H31.3756C32.3345 34.9999 33.2775 34.7547 34.1149 34.2876C34.9524 33.8206 35.6566 33.1472 36.1606 32.3314C36.6645 31.5156 36.9516 30.5845 36.9945 29.6265C37.0374 28.6686 36.8346 27.7156 36.4056 26.858L27.0316 8.108C26.5642 7.17394 25.846 6.38846 24.9574 5.83952C24.0689 5.29059 23.045 4.9999 22.0006 5Z"
      fill="white"
    />
  </svg>
);

const Avalanche = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#E84142",
    }}
  >
    <path
      d="M11.0188 32.1528H15.4825C16.5334 32.1528 17.0589 32.1528 17.5278 32.023C18.042 31.8701 18.511 31.5991 18.9009 31.2261C19.2589 30.885 19.5173 30.4328 20.0269 29.5409L20.0272 29.5404L20.0422 29.5142L25.8314 19.2804C26.3456 18.3821 26.5999 17.93 26.7129 17.4554C26.8372 16.9412 26.8372 16.3988 26.7129 15.8847C26.6007 15.4136 26.3439 14.9648 25.8373 14.0798L25.8258 14.0597L23.56 10.1045C23.0911 9.27958 22.8538 8.86711 22.5543 8.71456C22.2323 8.55071 21.848 8.55071 21.526 8.71456C21.2265 8.86711 20.9892 9.27958 20.5202 10.1045L9.49892 29.5311C9.03561 30.3447 8.80392 30.7517 8.82089 31.0849C8.84349 31.4466 9.02994 31.7743 9.33507 31.9721C9.61756 32.1528 10.0809 32.1528 11.0188 32.1528Z"
      fill="white"
    />
    <path
      d="M33.1506 32.1528H26.7547C25.8111 32.1528 25.3365 32.1528 25.0596 31.9721C24.7545 31.7743 24.5681 31.4411 24.5455 31.0794C24.5286 30.7486 24.7621 30.3456 25.2294 29.539L25.2295 29.5388L25.2404 29.5199L28.4328 24.0392C28.9018 23.2313 29.1391 22.8301 29.4329 22.6776C29.7548 22.5137 30.1336 22.5137 30.4555 22.6776C30.7472 22.8261 30.9744 23.2102 31.4241 23.9708L31.4248 23.9719L31.4613 24.0336L34.665 29.5143C34.6806 29.5413 34.696 29.5678 34.7113 29.5939L34.7113 29.594C35.1554 30.3603 35.382 30.7514 35.3657 31.0739C35.3486 31.4353 35.1566 31.7688 34.8515 31.9666C34.5689 32.1528 34.0942 32.1528 33.1506 32.1528Z"
      fill="white"
    />
  </svg>
);

const Celo = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#FCFE72",
    }}
  >
    <path
      d="M9 9H34.5183V18.112H30.3564C28.896 14.7687 25.6102 12.4171 21.777 12.4171C16.593 12.4171 12.3948 16.6422 12.3948 21.823C12.3948 27.0039 16.593 31.2654 21.777 31.2654C25.5373 31.2654 28.8231 28.9876 30.2829 25.7172H34.5178V34.682H9V9Z"
      fill={testnet ? "#ffffff" : "black"}
    />
  </svg>
);

const Gnosis = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#009CB4",
    }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.3439 11.8664C17.9374 6.53462 26.7953 6.74397 32.1271 12.3374C32.4738 12.7038 32.8075 13.0832 33.1084 13.4823L22 24.5972L10.8916 13.4823C11.1991 13.0832 11.5262 12.7038 11.8729 12.3374C12.0234 12.1804 12.1804 12.0234 12.3439 11.8664ZM30.6094 13.3972C28.3196 11.0944 25.271 9.83182 22 9.83182C18.729 9.83182 15.6804 11.0944 13.3907 13.3972L22 22.0066L30.6094 13.3972ZM33.9785 14.7446L31.7215 17.0016C33.5402 19.1801 33.2523 22.425 31.0738 24.2437C29.1636 25.84 26.3897 25.84 24.4794 24.2437L22 26.7231L19.5271 24.2502C17.3486 26.0689 14.1037 25.7811 12.285 23.6026C10.6888 21.6923 10.6888 18.9185 12.285 17.0082L11.1271 15.8502L10.028 14.7446C8.7 16.9297 8 19.4418 8 21.9998C8 29.7325 14.2673 35.9998 22 35.9998C29.7327 35.9998 36 29.7325 36 21.9998C36.0065 19.4418 35.3 16.9297 33.9785 14.7446ZM30.6486 18.0747C31.1392 18.7093 31.4075 19.4943 31.4075 20.299C31.4075 21.1037 31.1392 21.8887 30.6486 22.5233C29.4187 24.113 27.1355 24.4074 25.5458 23.1775L30.6486 18.0747ZM18.4542 23.1839C17.8196 23.6745 17.0346 23.9427 16.2299 23.9427C15.4252 23.9427 14.6467 23.6745 14.0056 23.1904C12.4159 21.9605 12.1215 19.6708 13.3514 18.0811L18.4542 23.1839Z"
      fill="white"
    />
  </svg>
);

const Evmos = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#2D2A25",
    }}
  >
    <path
      d="M18.4916 12.6668C12.9416 14.806 12.4332 20.2846 10.8418 22.8432C9.23155 25.4322 5.54251 26.8607 6.04698 28.1801C6.55143 29.4994 10.2449 28.0824 13.1669 28.9242C16.0543 29.7561 20.0831 33.4862 25.633 31.3469C28.4603 30.2573 30.5076 28.0143 31.449 25.3574C31.5502 25.0723 31.361 24.7673 31.0606 24.7391C30.874 24.7215 30.6948 24.8196 30.6106 24.9877C29.759 26.6908 28.2981 28.0934 26.3864 28.8301C23.2303 30.0465 19.777 29.0915 17.6562 26.6961C17.1746 26.1522 16.7626 25.533 16.4374 24.8487C16.348 24.6603 16.2629 24.4689 16.1875 24.2708C16.1117 24.0728 16.0473 23.8735 15.9881 23.6732C17.6562 22.8925 19.5812 22.0656 21.7635 21.2246C23.903 20.3999 25.8505 19.731 27.5841 19.1958C28.7571 18.8341 29.8322 18.5331 30.8029 18.2871C30.8732 18.2695 30.9423 18.2519 31.0112 18.2347C31.158 18.1982 31.3088 18.2769 31.363 18.4186L31.364 18.4213C31.396 18.5053 31.4236 18.5898 31.4535 18.6743C31.6453 19.2196 31.7892 19.7706 31.8841 20.3229C31.9258 20.5645 32.1888 20.6961 32.4044 20.5799C33.2014 20.1504 33.9302 19.7314 34.5814 19.3283C37.0083 17.8276 38.3538 16.5549 38.0776 15.8336C37.802 15.1119 35.9541 15.0705 33.1503 15.5854C32.2593 15.7491 31.2716 15.9691 30.207 16.2416C30.0229 16.2886 29.8365 16.3375 29.6481 16.3877C28.7522 16.6262 27.8073 16.8995 26.8234 17.2053C24.9936 17.7744 23.0305 18.4561 21.0038 19.2372C19.1078 19.9682 17.3109 20.726 15.6629 21.4812C15.6428 18.2761 17.5725 15.2461 20.7286 14.0297C22.6399 13.293 24.6605 13.3533 26.4285 14.0473C26.6029 14.116 26.8015 14.0684 26.9291 13.9298C27.1331 13.7076 27.0706 13.3537 26.8053 13.2094C24.3353 11.8685 21.319 11.5771 18.4916 12.6668Z"
      fill="#FAF1E4"
    />
  </svg>
);

const BinanceSmartChain = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    version="1.1"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 2496 2496"
  >
    <g>
      <path
        style={{ fillRule: "evenodd", clipRule: "evenodd", fill: "#F0B90B" }}
        d="M1248,0c689.3,0,1248,558.7,1248,1248s-558.7,1248-1248,1248
		S0,1937.3,0,1248S558.7,0,1248,0L1248,0z"
      />
      <path
        style={{ fill: "#FFFFFF" }}
        d="M685.9,1248l0.9,330l280.4,165v193.2l-444.5-260.7v-524L685.9,1248L685.9,1248z M685.9,918v192.3
		l-163.3-96.6V821.4l163.3-96.6l164.1,96.6L685.9,918L685.9,918z M1084.3,821.4l163.3-96.6l164.1,96.6L1247.6,918L1084.3,821.4
		L1084.3,821.4z"
      />
      <path
        style={{ fill: "#FFFFFF" }}
        d="M803.9,1509.6v-193.2l163.3,96.6v192.3L803.9,1509.6L803.9,1509.6z M1084.3,1812.2l163.3,96.6
		l164.1-96.6v192.3l-164.1,96.6l-163.3-96.6V1812.2L1084.3,1812.2z M1645.9,821.4l163.3-96.6l164.1,96.6v192.3l-164.1,96.6V918
		L1645.9,821.4L1645.9,821.4L1645.9,821.4z M1809.2,1578l0.9-330l163.3-96.6v524l-444.5,260.7v-193.2L1809.2,1578L1809.2,1578
		L1809.2,1578z"
      />
      <polygon
        style={{ fill: "#FFFFFF" }}
        points="1692.1,1509.6 1528.8,1605.3 1528.8,1413 1692.1,1316.4 1692.1,1509.6 	"
      />
      <path
        style={{ fill: "#FFFFFF" }}
        d="M1692.1,986.4l0.9,193.2l-281.2,165v330.8l-163.3,95.7l-163.3-95.7v-330.8l-281.2-165V986.4
		L968,889.8l279.5,165.8l281.2-165.8l164.1,96.6H1692.1L1692.1,986.4z M803.9,656.5l443.7-261.6l444.5,261.6l-163.3,96.6
		l-281.2-165.8L967.2,753.1L803.9,656.5L803.9,656.5z"
      />
    </g>
  </svg>
);

export const Solana = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="200" height="200" rx="100" fill="#121212" />
    <g clipPath="url(#clip0_295_42)">
      <path
        d="M149.48 125.382L132.807 142.801C132.444 143.18 132.006 143.482 131.519 143.688C131.031 143.894 130.505 144 129.974 144H50.9356C50.5585 144 50.1896 143.893 49.8742 143.691C49.5588 143.49 49.3107 143.203 49.1604 142.866C49.0101 142.529 48.9641 142.157 49.028 141.795C49.092 141.432 49.2631 141.096 49.5204 140.828L66.2061 123.408C66.5676 123.031 67.0047 122.729 67.4904 122.523C67.9762 122.317 68.5002 122.21 69.0301 122.21H148.064C148.441 122.21 148.81 122.317 149.126 122.518C149.441 122.72 149.689 123.007 149.84 123.344C149.99 123.681 150.036 124.053 149.972 124.415C149.908 124.777 149.737 125.113 149.48 125.382ZM132.807 90.3032C132.444 89.9248 132.006 89.6231 131.519 89.4169C131.031 89.2108 130.505 89.1045 129.974 89.1048H50.9356C50.5585 89.1048 50.1896 89.2121 49.8742 89.4136C49.5588 89.6151 49.3107 89.9019 49.1604 90.2388C49.0101 90.5758 48.9641 90.9482 49.028 91.3103C49.092 91.6723 49.2631 92.0083 49.5204 92.277L66.2061 109.697C66.5676 110.074 67.0047 110.375 67.4904 110.581C67.9762 110.788 68.5002 110.894 69.0301 110.895H148.064C148.441 110.895 148.81 110.788 149.126 110.586C149.441 110.385 149.689 110.098 149.84 109.761C149.99 109.424 150.036 109.052 149.972 108.69C149.908 108.328 149.737 107.992 149.48 107.723L132.807 90.3032ZM50.9356 77.7905H129.974C130.505 77.7907 131.031 77.6845 131.519 77.4783C132.006 77.2721 132.444 76.9704 132.807 76.592L149.48 59.1722C149.737 58.9036 149.908 58.5676 149.972 58.2055C150.036 57.8434 149.99 57.471 149.84 57.1341C149.689 56.7971 149.441 56.5103 149.126 56.3088C148.81 56.1073 148.441 56 148.064 56H69.0301C68.5002 56.0009 67.9762 56.1077 67.4904 56.3138C67.0047 56.52 66.5676 56.8211 66.2061 57.1985L49.5247 74.6183C49.2677 74.8866 49.0966 75.2223 49.0325 75.5839C48.9684 75.9456 49.0141 76.3177 49.1639 76.6545C49.3136 76.9913 49.5611 77.2781 49.8758 77.4799C50.1905 77.6817 50.5589 77.7896 50.9356 77.7905Z"
        fill="url(#paint0_linear_295_42)"
      />
    </g>
    <defs>
      <linearGradient
        id="paint0_linear_295_42"
        x1="57.5256"
        y1="146.097"
        x2="137.993"
        y2="52.9838"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.08" stopColor="#9945FF" />
        <stop offset="0.3" stopColor="#8752F3" />
        <stop offset="0.5" stopColor="#5497D5" />
        <stop offset="0.6" stopColor="#43B4CA" />
        <stop offset="0.72" stopColor="#28E0B9" />
        <stop offset="0.97" stopColor="#19FB9B" />
      </linearGradient>
      <clipPath id="clip0_295_42">
        <rect
          width="101"
          height="88"
          fill="white"
          transform="translate(49 56)"
        />
      </clipPath>
    </defs>
  </svg>
);

export const Bitcoin = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M43.3388 27.3219C40.4005 39.1077 28.4634 46.2804 16.6762 43.3413C4.89383 40.4029 -2.27886 28.4652 0.660895 16.68C3.59789 4.89286 15.535 -2.28052 27.3187 0.657859C39.1052 3.59623 46.2772 15.5354 43.3388 27.3219Z"
      fill="#F7931A"
    />
    <path
      d="M31.6992 18.8656C32.1371 15.9382 29.9082 14.3645 26.8605 13.3147L27.8492 9.34922L25.4353 8.74765L24.4728 12.6087C23.8383 12.4505 23.1865 12.3013 22.5389 12.1535L23.5083 8.26709L21.0958 7.66553L20.1065 11.6297C19.5813 11.51 19.0657 11.3918 18.5652 11.2673L18.5679 11.255L15.239 10.4238L14.5969 13.0019C14.5969 13.0019 16.3878 13.4123 16.35 13.4378C17.3277 13.6818 17.5043 14.3288 17.4748 14.8417L16.3487 19.3592C16.416 19.3764 16.5033 19.4012 16.5996 19.4397C16.5192 19.4197 16.4332 19.3977 16.3445 19.3764L14.766 25.7048C14.6464 26.0018 14.3432 26.4473 13.6598 26.2782C13.6839 26.3133 11.9053 25.8403 11.9053 25.8403L10.707 28.6033L13.8482 29.3864C14.4326 29.5328 15.0053 29.6862 15.569 29.8305L14.5701 33.8414L16.9812 34.443L17.9705 30.4747C18.6291 30.6535 19.2685 30.8185 19.8941 30.9738L18.9082 34.9235L21.322 35.5251L22.321 31.5218C26.437 32.3007 29.5322 31.9865 30.835 28.2637C31.8848 25.2662 30.7827 23.5372 28.6171 22.4097C30.1942 22.046 31.3822 21.0085 31.6992 18.8656ZM26.184 26.5993C25.4381 29.5968 20.3912 27.9763 18.7549 27.57L20.0804 22.2563C21.7167 22.6647 26.9637 23.4732 26.184 26.5993ZM26.9307 18.8223C26.25 21.5489 22.0494 20.1636 20.6868 19.824L21.8885 15.0046C23.2512 15.3442 27.6395 15.9781 26.9307 18.8223Z"
      fill="white"
    />
  </svg>
);

export const Tron = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_450_171)">
      <path
        d="M43.3388 27.3219C40.4005 39.1077 28.4634 46.2804 16.6762 43.3413C4.89383 40.4029 -2.27886 28.4652 0.660896 16.68C3.59789 4.89286 15.535 -2.28052 27.3187 0.65786C39.1052 3.59623 46.2772 15.5354 43.3388 27.3219Z"
        fill="#FF060A"
      />
      <path
        d="M36.3975 17.2381C35.0196 16.0232 33.1057 14.1724 31.5555 12.8625L31.4598 12.8056C31.3067 12.6917 31.1344 12.5968 30.9526 12.5303C27.2015 11.8659 9.74716 8.76218 9.41224 8.80015C9.31654 8.80964 9.22085 8.84761 9.1443 8.89507L9.05817 8.96151C8.95291 9.06591 8.86679 9.1893 8.81894 9.33167L8.7998 9.38862V9.70184V9.7493C10.7615 14.9507 18.5222 31.9785 20.0532 35.9839C20.1489 36.2591 20.3212 36.7717 20.6465 36.8001H20.7231C20.8953 36.8001 21.6417 35.8605 21.6417 35.8605C21.6417 35.8605 34.9717 20.5032 36.321 18.8707C36.4932 18.6713 36.6463 18.453 36.7803 18.2252C36.8186 18.0449 36.7994 17.8646 36.7324 17.6937C36.6655 17.5229 36.5411 17.3615 36.3975 17.2381ZM25.0484 19.032L30.7325 14.552L34.0722 17.4754L25.0484 19.032ZM22.8379 18.7378L13.0486 11.1066L28.8952 13.8876L22.8379 18.7378ZM23.7183 20.731L33.7373 19.1934L22.2829 32.3202L23.7183 20.731ZM11.7184 11.8754L22.0245 20.1805L20.5317 32.3296L11.7184 11.8754Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_450_171">
        <rect width="44" height="44" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const Zcash = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M43.3415 27.3221C40.4029 39.1078 28.4645 46.2804 16.676 43.3413C4.8924 40.403 -2.28106 28.4654 0.659014 16.6803C3.59632 4.8933 15.5347 -2.28001 27.3197 0.658341C39.1074 3.59668 46.2802 15.5357 43.3415 27.3221Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 22C0 9.86741 9.86741 0 22 0C34.1326 0 44 9.86741 44 22C44 34.1326 34.1326 44 22 44C9.86741 44 0 34.1326 0 22ZM29.8475 11.7904V15.1384L20.5358 27.7681H29.8475V32.2088H23.8447V35.8885H20.1553V32.2088H14.1525V28.8607L23.4544 16.2311H14.1525V11.7904H20.1553V8.10089H23.8447V11.7904H29.8475Z"
      fill="#F4B728"
    />
  </svg>
);

const Canto = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "white",
    }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M35 8V36H9L13.875 35.9998V31.0586H9V12.9412H13.875V8H35ZM17.9373 12.9414H30.1247V17.8826H17.9373V12.9414ZM30.1247 26.9414H17.9373V17.8826L13.0623 17.8828V26.9416L17.9373 26.9414V31.8826H30.1247V26.9414Z"
      fill="#06FC99"
    />
  </svg>
);

const Fantom = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#0911EF",
    }}
  >
    <path
      d="M20.92 9.25864C21.5933 8.91379 22.6178 8.91379 23.2911 9.25864L30.1616 12.7775C30.5671 12.9852 30.7898 13.2947 30.8297 13.6142H30.8363V31.302C30.8274 31.6504 30.6025 31.9966 30.1616 32.2225L23.2911 35.7413C22.6178 36.0862 21.5933 36.0862 20.92 35.7413L14.0495 32.2225C13.6104 31.9976 13.3997 31.6489 13.3893 31.302C13.3883 31.2678 13.3881 31.2393 13.3891 31.2157L13.3891 13.7278C13.3884 13.7086 13.3883 13.6895 13.3889 13.6705L13.3893 13.6142L13.3924 13.6142C13.4229 13.2912 13.6355 12.9896 14.0495 12.7775L20.92 9.25864ZM29.7547 23.4821L23.2911 26.7926C22.6178 27.1374 21.5933 27.1374 20.92 26.7926L14.4706 23.4895V31.2669L20.92 34.5527C21.2842 34.7415 21.6622 34.9254 22.0318 34.9488L22.1056 34.9512C22.4907 34.9524 22.8646 34.7628 23.2438 34.5833L29.7547 31.2387V23.4821ZM11.3214 31.8437C11.3214 32.5212 11.4026 32.9667 11.5639 33.2806C11.6976 33.5407 11.8981 33.7394 12.2643 33.9813L12.2852 33.9951C12.3656 34.0476 12.4541 34.1026 12.5619 34.1672L12.689 34.2427L13.0792 34.4711L12.5195 35.3685L12.0827 35.1126L12.0093 35.0689C11.883 34.9932 11.7783 34.9284 11.6807 34.8645C10.637 34.1822 10.2478 33.4384 10.2401 31.8907L10.24 31.8437H11.3214ZM21.5647 18.7412C21.5147 18.7579 21.4678 18.7772 21.4251 18.7991L14.5546 22.318C14.5474 22.3216 14.5405 22.3253 14.534 22.3289L14.5281 22.3322L14.5389 22.3382L14.5546 22.3464L21.4251 25.8653C21.4678 25.8872 21.5147 25.9065 21.5647 25.9231V18.7412ZM22.6465 18.7412V25.9231C22.6965 25.9065 22.7433 25.8872 22.7861 25.8653L29.6566 22.3464C29.6638 22.3427 29.6707 22.3391 29.6772 22.3355L29.683 22.3322L29.6722 22.3262L29.6566 22.318L22.7861 18.7991C22.7433 18.7772 22.6965 18.7579 22.6465 18.7412ZM29.7547 14.8689L23.5915 18.0256L29.7547 21.1822V14.8689ZM14.4706 14.8763V21.1749L20.6195 18.0256L14.4706 14.8763ZM22.7861 10.1859C22.4288 10.0029 21.7824 10.0029 21.4251 10.1859L14.5546 13.7048C14.5474 13.7085 14.5405 13.7122 14.534 13.7158L14.5281 13.719L14.5389 13.725L14.5546 13.7333L21.4251 17.2522C21.7824 17.4352 22.4288 17.4352 22.7861 17.2522L29.6566 13.7333C29.6638 13.7296 29.6707 13.7259 29.6772 13.7223L29.683 13.719L29.6722 13.7131L29.6566 13.7048L22.7861 10.1859ZM31.7205 9.64552L32.1573 9.90132L32.2307 9.94503C32.357 10.0206 32.4616 10.0856 32.5593 10.1494C33.603 10.8317 33.9922 11.5756 33.9998 13.1231L34 13.1703H32.9186C32.9186 12.4926 32.8373 12.0472 32.6761 11.7334C32.5424 11.4733 32.3419 11.2745 31.9757 11.0327L31.9547 11.0189C31.8744 10.9664 31.7858 10.9113 31.6781 10.8466L31.551 10.7712L31.1608 10.5428L31.7205 9.64552Z"
      fill="white"
    />
  </svg>
);

const Filecoin = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#0090FF",
    }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.75 19.3069L23.15 22.5069L28.85 23.3069L28.45 24.8069L22.85 24.0069C22.45 25.3069 22.25 26.7069 21.75 27.9069C21.25 29.3069 20.75 30.7069 20.15 32.0069C19.35 33.7069 17.95 34.9069 16.05 35.2069C14.95 35.4069 13.75 35.3069 12.85 34.6069C12.55 34.4069 12.25 34.0069 12.25 33.7069C12.25 33.3069 12.45 32.8069 12.75 32.6069C12.95 32.5069 13.45 32.6069 13.75 32.7069C14.05 33.0069 14.35 33.4069 14.55 33.8069C15.15 34.6069 15.95 34.7069 16.75 34.1069C17.65 33.3069 18.15 32.2069 18.45 31.1069C19.05 28.7069 19.65 26.4069 20.15 24.0069V23.6069L14.85 22.8069L15.05 21.3069L20.55 22.1069L21.25 19.0069L15.55 18.1069L15.75 16.5069L21.65 17.3069C21.85 16.7069 21.95 16.2069 22.15 15.7069C22.65 13.9069 23.15 12.1069 24.35 10.5069C25.55 8.90687 26.95 7.90687 29.05 8.00687C29.95 8.00687 30.85 8.30687 31.45 9.00687C31.55 9.10687 31.75 9.30687 31.75 9.50687C31.75 9.90687 31.75 10.4069 31.45 10.7069C31.05 11.0069 30.55 10.9069 30.15 10.5069C29.85 10.2069 29.65 9.90687 29.35 9.60687C28.75 8.80687 27.85 8.70687 27.15 9.40687C26.65 9.90687 26.15 10.6069 25.85 11.3069C25.15 13.4069 24.65 15.6069 23.95 17.8069L29.45 18.6069L29.05 20.1069L23.75 19.3069Z"
      fill="white"
    />
  </svg>
);

const IoTeX = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#00D4D5",
    }}
  >
    <path
      d="M23.7136 6.875V14.3784L30.2284 10.6315L23.7136 6.875Z"
      fill="white"
    />
    <path
      opacity="0.9"
      d="M30.2284 10.6316V18.135L36.7418 14.3785L30.2284 10.6316Z"
      fill="white"
    />
    <path
      opacity="0.8"
      d="M23.7136 14.3784V21.8818L30.2284 18.1349L23.7136 14.3784ZM30.2284 18.1349V25.6383L36.7417 21.8818L30.2284 18.1349Z"
      fill="white"
    />
    <path
      opacity="0.8"
      d="M23.7136 21.8817V29.385L30.2284 25.6382L23.7136 21.8817Z"
      fill="white"
    />
    <path
      d="M30.2284 25.6382V33.1416L36.7418 29.3851L30.2284 25.6382Z"
      fill="white"
    />
    <path
      opacity="0.4"
      d="M6.87537 14.1253V21.6287L13.3901 17.8722L6.87537 14.1253Z"
      fill="white"
    />
    <path
      opacity="0.2"
      d="M15.0938 16.9153V24.4186L21.5975 20.6718L15.0938 16.9153Z"
      fill="white"
    />
    <path
      opacity="0.3"
      d="M10.2648 21.6604V29.1638L16.7781 25.4073L10.2648 21.6604Z"
      fill="white"
    />
    <path
      opacity="0.9"
      d="M14.5575 27.3226V34.826L21.0612 31.0695L14.5575 27.3226Z"
      fill="white"
    />
    <path
      opacity="0.7"
      d="M23.66 30.5525V38.0572L30.1637 34.2993L23.66 30.5525Z"
      fill="white"
    />
    <path
      opacity="0.9"
      d="M16.1786 13.2097V20.7145L22.6824 16.9676L16.1786 13.2097Z"
      fill="white"
    />
    <path
      opacity="0.8"
      d="M23.7136 6.875V14.3784L17.1989 10.6315L23.7136 6.875Z"
      fill="white"
    />
    <path
      opacity="0.6"
      d="M16.1786 10.0649V17.5669L9.66248 13.8104L16.1786 10.0649Z"
      fill="white"
    />
    <path
      opacity="0.6"
      d="M22.6934 13.7775V21.2823L16.1786 17.5244L22.6934 13.7775Z"
      fill="white"
    />
    <path
      opacity="0.95"
      d="M15.0635 16.9153V24.4186L8.54877 20.6718L15.0635 16.9153Z"
      fill="white"
    />
    <path
      opacity="0.6"
      d="M23.7136 21.8817V29.385L17.2099 25.6382L23.7136 21.8817Z"
      fill="white"
    />
    <path
      opacity="0.55"
      d="M10.2648 23.6295V31.1328L3.75 27.375L10.2648 23.6295Z"
      fill="white"
    />
    <path
      d="M36.7418 14.3784V21.8818L30.2284 18.1349L36.7418 14.3784Z"
      fill="white"
    />
    <path
      opacity="0.95"
      d="M30.2284 18.1362V25.6382L23.7136 21.8817L30.2284 18.1362Z"
      fill="white"
    />
    <path
      opacity="0.9"
      d="M36.7418 21.8817V29.385L30.2284 25.6382L36.7418 21.8817Z"
      fill="white"
    />
    <path
      opacity="0.7"
      d="M30.2284 25.6382V33.1416L23.7136 29.3851L30.2284 25.6382Z"
      fill="white"
    />
    <path
      opacity="0.4"
      d="M22.2712 28.7651V36.2684L15.7579 32.5216L22.2712 28.7651Z"
      fill="white"
    />
    <path
      d="M30.2284 10.6316V18.135L23.7136 14.3785L30.2284 10.6316Z"
      fill="white"
    />
  </svg>
);

const Metis = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#2F3140",
    }}
  >
    <path
      d="M37.5175 22.0531C37.4579 19.2923 36.6563 16.5985 35.1968 14.2542C33.7374 11.91 31.674 10.0017 29.223 8.72965C26.772 7.45759 24.0238 6.86863 21.2668 7.02455C18.5098 7.18047 15.8456 8.07553 13.5537 9.61582C11.2617 11.1561 9.42659 13.2849 8.24079 15.7787C7.05498 18.2725 6.56222 21.0396 6.81419 23.7895C7.06617 26.5394 8.05359 29.1708 9.67288 31.4076C11.2922 33.6444 13.4836 35.4042 16.0173 36.5023C16.7657 35.3775 17.3385 34.1453 17.716 32.848C18.0245 32.0632 18.3595 31.2913 18.7067 30.5446C19.5444 30.7955 20.4345 30.8143 21.282 30.5989C22.1295 30.3835 22.9026 29.942 23.5188 29.3215L23.5704 29.2699C22.8136 28.9884 21.9979 28.9032 21.1993 29.022C20.4006 29.1408 19.6451 29.4598 19.0029 29.9494C19.4276 29.0613 19.891 28.1997 20.3667 27.3632C21.189 27.6541 22.075 27.7156 22.9296 27.541C23.7842 27.3665 24.5751 26.9626 25.2175 26.3726L25.2692 26.3209C24.538 26.0116 23.7416 25.8885 22.9513 25.9626C22.1609 26.0368 21.4013 26.3058 20.7404 26.7456C21.216 25.9608 21.7053 25.1889 22.2203 24.468C23.0713 24.6915 23.9672 24.6777 24.811 24.4282C25.6547 24.1787 26.414 23.703 27.0066 23.0526L27.0453 23.001C26.3425 22.7718 25.5958 22.7106 24.8651 22.8224C24.1344 22.9341 23.4401 23.2157 22.838 23.6444C22.8767 23.5928 22.9283 23.5289 22.9664 23.4773C23.2749 23.0784 23.5969 22.6796 23.9177 22.2936C24.8969 21.9731 25.7703 21.3916 26.4436 20.6117C27.117 19.8318 27.5649 18.883 27.7391 17.8674L27.752 17.79H27.7391C26.7194 18.0552 25.7944 18.6007 25.0689 19.3648C24.3434 20.1288 23.8464 21.0808 23.6343 22.1129C23.3258 22.4859 23.0167 22.8603 22.7211 23.2449C22.9706 22.5925 23.0724 21.893 23.0191 21.1966C22.9657 20.5002 22.7586 19.8243 22.4126 19.2176L22.3739 19.2692C21.8489 19.9862 21.5326 20.8345 21.4599 21.7201C21.3873 22.6058 21.5611 23.4942 21.9621 24.2872C21.4729 24.9823 20.9972 25.6897 20.5467 26.4357C20.6918 25.6858 20.6542 24.9118 20.4369 24.1795C20.2196 23.4471 19.8291 22.7779 19.2985 22.2284L19.2727 22.2929C18.9445 23.1107 18.8477 24.0031 18.9929 24.8723C19.1382 25.7415 19.5199 26.5539 20.0962 27.2205C19.6457 28.0054 19.2211 28.816 18.8093 29.6524C18.9344 28.8712 18.8712 28.0715 18.6251 27.3196C18.3789 26.5677 17.9568 25.8855 17.3939 25.3295L17.3681 25.3941C17.0745 26.2514 17.0201 27.1724 17.2105 28.0583C17.401 28.9442 17.8292 29.7614 18.4492 30.4223C18.1774 31.0012 17.9219 31.5774 17.6773 32.1849C17.61 32.1231 17.5313 32.0751 17.4456 32.0435C17.0066 31.916 16.5867 31.7299 16.1974 31.4904C15.8754 31.2994 15.5337 31.144 15.1781 31.027C13.7886 30.5765 13.9945 29.9079 13.2756 28.5564C13.0416 28.2781 12.7931 28.0125 12.5309 27.7607C12.2876 27.603 12.0884 27.386 11.9519 27.1302C11.7934 26.8107 11.6931 26.4656 11.6557 26.1109C11.6514 25.9148 11.5858 25.7249 11.4681 25.5679C11.3504 25.4109 11.1864 25.2948 10.9993 25.2358C8.95338 24.5284 9.86728 21.8778 9.94406 21.5299C9.91262 21.08 9.81271 20.6375 9.64781 20.2178C9.63057 20.1632 9.61763 20.1073 9.60908 20.0506C9.55449 19.776 9.56444 19.4924 9.63813 19.2223C9.71183 18.9521 9.84726 18.7028 10.0338 18.4939C10.2784 18.2493 11.1277 17.8505 11.3078 17.6188C11.4879 17.387 11.6163 17.1295 11.797 16.9114C12.3687 16.3412 13.0291 15.8675 13.7526 15.509C14.3444 15.1876 14.4477 14.364 14.6787 14.1065C15.0647 13.6689 15.6565 13.6689 16.0941 13.283C16.3129 13.09 16.6377 12.9867 16.8415 12.7815C17.8692 11.8403 19.1644 11.2423 20.5474 11.0706C21.5064 10.9924 22.4717 11.0795 23.4012 11.3281C23.5358 11.3507 23.669 11.3809 23.8001 11.4185C25.9228 11.4959 28.4709 11.9328 29.397 12.8338C29.8555 13.2744 30.1961 13.8229 30.3877 14.4293C30.5382 14.8988 30.7456 15.3481 31.0054 15.7672L31.5714 16.6933C31.932 17.2773 32.0475 17.9801 31.8928 18.6489C31.7885 18.974 31.7795 19.3223 31.8669 19.6525C32.1655 20.1031 32.5152 20.5177 32.9093 20.8878C33.0658 21.0427 33.2382 21.1807 33.4236 21.2995C33.8592 21.5594 34.2802 21.8429 34.6848 22.1489C34.698 22.2729 34.6775 22.3982 34.6254 22.5115C34.5732 22.6247 34.4914 22.7218 34.3885 22.7924C34.0284 23.1137 33.3333 23.6159 33.3333 23.6159C33.3584 23.758 33.4016 23.8964 33.4617 24.0276C33.5643 24.2077 33.796 24.4911 33.7192 24.735C33.6424 24.9789 33.1403 25.1855 33.2816 25.4552C33.423 25.7386 33.7831 25.8154 33.6805 26.0729C33.5779 26.3175 33.0757 26.7524 33.1274 26.9352C33.179 27.1179 33.5908 28.8377 32.6382 29.1353C31.4592 29.3685 30.2627 29.5019 29.0614 29.5342C28.8675 29.5454 28.6797 29.6055 28.5155 29.709C28.3513 29.8125 28.216 29.956 28.1223 30.126C27.9399 30.4661 27.8222 30.837 27.7751 31.22C27.4275 32.5896 26.9671 33.928 26.3985 35.2215C26.3985 35.2215 26.3597 35.2989 26.3081 35.4144C26.1833 35.6687 26.1048 35.9432 26.0764 36.2251C26.1573 36.4321 26.2799 36.6204 26.4365 36.7782C26.519 36.8726 26.6271 36.9411 26.7477 36.9753C26.8683 37.0096 26.9962 37.0081 27.116 36.9711C30.1997 35.8965 32.8655 33.8757 34.7332 31.197C36.601 28.5182 37.5754 25.3182 37.5175 22.0531Z"
      fill={testnet ? "#ffffff" : "#00DACC"}
    />
  </svg>
);

export const Base = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      background: testnet
        ? "linear-gradient(180deg, #8995A9 0%, #424D5F 99.48%)"
        : "#0052FF",
    }}
  >
    <path
      d="M21.9756 36C29.721 36 36 29.732 36 22C36 14.268 29.721 8 21.9756 8C14.6271 8 8.59871 13.6419 8 20.8232H26.5371V23.1768H8C8.59871 30.3581 14.6271 36 21.9756 36Z"
      fill="white"
    />
  </svg>
);
const Zora = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      filter: testnet ? "grayscale(1)" : "none",
    }}
  >
    <mask
      id="ck_zora_mask_a"
      style={{
        maskType: "alpha",
      }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="44"
      height="44"
    >
      <path
        d="M22 44C34.1503 44 44 34.1503 44 22C44 9.84974 34.1503 0 22 0C9.84974 0 0 9.84974 0 22C0 34.1503 9.84974 44 22 44Z"
        fill="#D9D9D9"
      />
    </mask>
    <g mask="url(#ck_zora_mask_a)">
      <path
        d="M51.4558 -9.56445H-6.78906V48.6804H51.4558V-9.56445Z"
        fill="#A1723A"
      />
      <g filter="url(#ck_zora_filter_a)">
        <path
          d="M23.6807 43.0752C36.6464 43.0752 47.157 32.5675 47.157 19.6058C47.157 6.64397 36.6464 -3.86365 23.6807 -3.86365C10.7152 -3.86365 0.20459 6.64397 0.20459 19.6058C0.20459 32.5675 10.7152 43.0752 23.6807 43.0752Z"
          fill="#531002"
        />
      </g>
      <g filter="url(#ck_zora_filter_b)">
        <path
          d="M26.2112 35.6464C36.7271 35.6464 45.2521 27.1185 45.2521 16.5988C45.2521 6.07904 36.7271 -2.44885 26.2112 -2.44885C15.6953 -2.44885 7.17041 6.07904 7.17041 16.5988C7.17041 27.1185 15.6953 35.6464 26.2112 35.6464Z"
          fill="#2B5DF0"
        />
      </g>
      <g filter="url(#ck_zora_filter_c)">
        <path
          d="M25.8644 36.7348C36.8276 36.7348 45.7149 27.8444 45.7149 16.8777C45.7149 5.91084 36.8276 -2.97949 25.8644 -2.97949C14.9015 -2.97949 6.01416 5.91084 6.01416 16.8777C6.01416 27.8444 14.9015 36.7348 25.8644 36.7348Z"
          fill="url(#paint0_radial_3914_1946)"
        />
      </g>
      <g filter="url(#ck_zora_filter_d)">
        <path
          d="M29.1567 21.8779C34.6797 21.8779 39.1567 17.4008 39.1567 11.8779C39.1567 6.35509 34.6797 1.87793 29.1567 1.87793C23.6338 1.87793 19.1567 6.35509 19.1567 11.8779C19.1567 17.4008 23.6338 21.8779 29.1567 21.8779Z"
          fill="#FCB8D4"
        />
      </g>
      <g filter="url(#ck_zora_filter_e)">
        <path
          d="M29.15 15.8642C31.3555 15.8642 33.1432 14.0765 33.1432 11.871C33.1432 9.66562 31.3555 7.87781 29.15 7.87781C26.9445 7.87781 25.1567 9.66562 25.1567 11.871C25.1567 14.0765 26.9445 15.8642 29.15 15.8642Z"
          fill="white"
        />
      </g>
      <g filter="url(#ck_zora_filter_f)">
        <path
          d="M26.4967 51.7416C46.3151 51.7416 62.3811 35.6757 62.3811 15.8573C62.3811 -3.96109 46.3151 -20.0271 26.4967 -20.0271C6.67829 -20.0271 -9.3877 -3.96109 -9.3877 15.8573C-9.3877 35.6757 6.67829 51.7416 26.4967 51.7416Z"
          fill="url(#paint1_radial_3914_1946)"
          fillOpacity="0.9"
        />
      </g>
    </g>
    <defs>
      <filter
        id="ck_zora_filter_a"
        x="-5.23758"
        y="-9.30581"
        width="57.837"
        height="57.8232"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="2.72108"
          result="effect1_foregroundBlur_3914_1946"
        />
      </filter>
      <filter
        id="ck_zora_filter_b"
        x="-3.71395"
        y="-13.3332"
        width="59.8503"
        height="59.8639"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="5.44218"
          result="effect1_foregroundBlur_3914_1946"
        />
      </filter>
      <filter
        id="ck_zora_filter_c"
        x="1.93251"
        y="-7.06114"
        width="47.864"
        height="47.8775"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="2.04082"
          result="effect1_foregroundBlur_3914_1946"
        />
      </filter>
      <filter
        id="ck_zora_filter_d"
        x="10.9935"
        y="-6.28533"
        width="36.3265"
        height="36.3265"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="4.08163"
          result="effect1_foregroundBlur_3914_1946"
        />
      </filter>
      <filter
        id="ck_zora_filter_e"
        x="19.7146"
        y="2.43564"
        width="18.8707"
        height="18.8708"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="2.72108"
          result="effect1_foregroundBlur_3914_1946"
        />
      </filter>
      <filter
        id="ck_zora_filter_f"
        x="-13.4693"
        y="-24.1087"
        width="79.9318"
        height="79.9321"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="2.04082"
          result="effect1_foregroundBlur_3914_1946"
        />
      </filter>
      <radialGradient
        id="paint0_radial_3914_1946"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(29.2127 11.2756) rotate(128.228) scale(37.4897 37.4867)"
      >
        <stop offset="0.286458" stopColor="#387AFA" />
        <stop offset="0.647782" stopColor="#387AFA" stopOpacity="0" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_3914_1946"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(26.4967 15.8573) rotate(90) scale(35.8844 35.8844)"
      >
        <stop offset="0.598958" stopOpacity="0" />
        <stop offset="0.671875" />
        <stop offset="0.734375" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);

const Linea = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    width="44"
    height="44"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M200 0H0V200H200V0Z" fill="#121212" />
    <mask
      id="mask0_267_10"
      style={{ maskType: "luminance" }}
      maskUnits="userSpaceOnUse"
      x="50"
      y="48"
      width="100"
      height="104"
    >
      <path d="M149.541 48H50V151.977H149.541V48Z" fill="white" />
    </mask>
    <g mask="url(#mask0_267_10)">
      <path
        d="M132.669 151.977H50V64.8721H68.9147V135.096H132.669V151.968V151.977Z"
        fill="white"
      />
      <path
        d="M132.669 81.7441C141.987 81.7441 149.541 74.1902 149.541 64.8721C149.541 55.5539 141.987 48 132.669 48C123.35 48 115.797 55.5539 115.797 64.8721C115.797 74.1902 123.35 81.7441 132.669 81.7441Z"
        fill="white"
      />
    </g>
  </svg>
);

const Worldchain = ({ testnet, ...props }: { testnet?: boolean }) => (
  <svg
    {...props}
    aria-hidden="true"
    width="44"
    height="44"
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 0 C132 0 264 0 400 0 C400 132 400 264 400 400 C268 400 136 400 0 400 C0 268 0 136 0 0 Z "
      fill="#FEFEFE"
      transform="translate(0,0)"
    />
    <path
      d="M0 0 C0.70403961 0.0012085 1.40807922 0.00241699 2.13345337 0.00366211 C37.34271908 0.16721508 68.5105973 14.80090333 93.17163086 39.68505859 C98.62984305 45.41558542 103.09177892 51.78200535 107.4375 58.375 C108.159375 59.4475 108.88125 60.52 109.625 61.625 C122.08220864 80.87704972 127.84793078 103.26191133 127.75 126.0625 C127.74850952 126.72419189 127.74701904 127.38588379 127.7454834 128.06762695 C127.70436084 138.22551322 127.07498098 147.56085403 124.4375 157.375 C124.14746094 158.56480469 123.85742188 159.75460938 123.55859375 160.98046875 C117.33294616 184.86715846 102.42700827 208.48904018 83.4375 224.375 C82.7775 224.375 82.1175 224.375 81.4375 224.375 C81.4375 225.035 81.4375 225.695 81.4375 226.375 C79.55168751 227.86844182 77.6802053 229.22460868 75.6875 230.5625 C75.09767334 230.96251221 74.50784668 231.36252441 73.90014648 231.7746582 C45.33982467 250.94089101 10.69801357 259.36417248 -23.53125 253.31640625 C-58.32294327 245.96067382 -87.68703658 225.97193245 -107.32177734 196.33154297 C-113.84308531 186.04784324 -119.21618764 175.09939589 -122.5625 163.375 C-122.75714844 162.7032373 -122.95179687 162.03147461 -123.15234375 161.33935547 C-126.29298989 150.01096274 -126.95426502 138.7553544 -127 127.0625 C-127.0061734 126.39534973 -127.0123468 125.72819946 -127.01870728 125.04083252 C-127.22210042 90.87814217 -112.10569979 60.09763142 -88.5625 35.9375 C-73.41024939 20.79283974 -54.8893351 10.80975271 -34.5625 4.375 C-33.52222656 4.04242188 -32.48195313 3.70984375 -31.41015625 3.3671875 C-20.96204118 0.67295275 -10.76743051 -0.03432509 0 0 Z "
      fill="#030303"
      transform="translate(199.5625,72.625)"
    />
    <path
      d="M0 0 C11.55 0 23.1 0 35 0 C37.475 7.425 37.475 7.425 40 15 C49.21380616 33.94914853 62.0370052 48.14330377 82.26171875 55.140625 C83.16535156 55.42421875 84.06898438 55.7078125 85 56 C86.03415543 56.34114838 87.06831085 56.68229675 88.13380432 57.03378296 C92.4151578 58.10375381 96.2112859 58.30653699 100.59741211 58.31884766 C101.84252563 58.33391609 101.84252563 58.33391609 103.11279297 58.34928894 C105.83430337 58.37997941 108.55571605 58.39711649 111.27734375 58.4140625 C113.17306857 58.43278554 115.06878515 58.45235979 116.9644928 58.4727478 C121.93741998 58.52395501 126.91037415 58.56364168 131.88342285 58.60089111 C136.96466688 58.64100246 142.04579976 58.69206221 147.12695312 58.7421875 C157.08455721 58.83881161 167.04222421 58.92325948 177 59 C176.06240625 60.11818809 175.12128368 61.23341782 174.17871094 62.34741211 C173.39315826 63.27945717 173.39315826 63.27945717 172.59173584 64.23033142 C162.58148353 75.35958323 147.85650103 82.10162688 134 87 C132.87980469 87.41765625 131.75960937 87.8353125 130.60546875 88.265625 C112.47396566 93.94296445 87.79207496 93.59729315 70 87 C68.77410156 86.5875 67.54820312 86.175 66.28515625 85.75 C40.7576163 76.74144211 20.58906247 57.52681738 8.6484375 33.45703125 C3.51705369 22.69571686 0 12.06068152 0 0 Z "
      fill="#FAFAFA"
      transform="translate(98,212)"
    />
    <path
      d="M0 0 C0.99128906 0.26683594 1.98257813 0.53367187 3.00390625 0.80859375 C20.02205338 5.90723969 35.38784974 15.53622797 48 28 C48 28.33 48 28.66 48 29 C46.99569946 29.00142502 45.99139893 29.00285004 44.95666504 29.00431824 C35.42537052 29.02109205 25.89449728 29.06417913 16.36342621 29.13209057 C11.46504263 29.16617258 6.56696472 29.19144103 1.66845703 29.19555664 C-3.07161547 29.19988029 -7.81100063 29.22867466 -12.55085754 29.27343178 C-14.34621658 29.28627824 -16.14165841 29.29076089 -17.9370575 29.28615379 C-39.57125354 29.2436265 -58.64662622 35.40002391 -74.56494141 50.54882812 C-85.14790619 61.25062171 -89.66973578 73.56885506 -93 88 C-104.88 88 -116.76 88 -129 88 C-129 64.08086196 -115.40828709 41.63583644 -99 25 C-72.65926108 0.43663887 -35.01441399 -9.99793527 0 0 Z "
      fill="#FBFBFB"
      transform="translate(227,100)"
    />
    <path
      d="M0 0 C1.43530815 -0.00043808 1.43530815 -0.00043808 2.89961243 -0.00088501 C6.04327657 0.00050157 9.18669983 0.01603116 12.33032227 0.03173828 C14.51750051 0.0354708 16.70468044 0.03831664 18.89186096 0.04031372 C24.63427386 0.04793137 30.37658477 0.06756384 36.11895752 0.08972168 C41.98432028 0.11022036 47.8496996 0.11932403 53.71508789 0.12939453 C65.21317945 0.15081148 76.71119548 0.18492487 88.20922852 0.22705078 C89.01417532 1.6299547 89.81343075 3.03612493 90.61035156 4.44360352 C91.05606003 5.22630112 91.50176849 6.00899872 91.96098328 6.81541443 C93.86879215 10.50134175 94.96928018 14.29024711 96.02172852 18.28955078 C96.34560547 19.41586914 96.34560547 19.41586914 96.67602539 20.56494141 C98.11972655 25.86767867 98.20922852 29.34376842 98.20922852 35.22705078 C50.68922852 35.22705078 3.16922852 35.22705078 -45.79077148 35.22705078 C-41.98207673 22.53140161 -35.92269341 12.61991806 -24.31030273 5.76220703 C-16.09258333 1.47307966 -9.18687217 -0.07412284 0 0 Z "
      fill="#FDFDFD"
      transform="translate(203.790771484375,152.77294921875)"
    />
    <path
      d="M0 0 C47.52 0 95.04 0 144 0 C142.84140283 13.90316605 142.84140283 13.90316605 140.6875 20.3125 C140.48705078 20.96283203 140.28660156 21.61316406 140.08007812 22.28320312 C138.58431084 26.91869244 136.43289036 30.78177675 134 35 C121.42969934 35.09296032 108.85948139 35.16389686 96.2889061 35.20724869 C90.45094348 35.22806258 84.61321724 35.25626666 78.77539062 35.30175781 C73.1343582 35.34543695 67.49355217 35.36918365 61.85236359 35.37950897 C59.70752655 35.38686085 57.56270095 35.40121672 55.41796112 35.42292023 C38.21797933 35.58994386 24.82919001 34.23919899 11.77734375 21.8984375 C5.69643497 15.465177 1.74000471 8.70002355 0 0 Z "
      fill="#FDFDFD"
      transform="translate(158,212)"
    />
    <path
      d="M0 0 C47.52 0 95.04 0 144 0 C144 0.33 144 0.66 144 1 C143.27516733 1.00238835 142.55033466 1.00477671 141.80353737 1.00723743 C124.18601629 1.0663125 106.5686429 1.14181412 88.95127296 1.23571491 C80.43172058 1.28072596 71.91221049 1.32012822 63.39257812 1.34643555 C55.96891368 1.3693728 48.5453825 1.40272254 41.12182367 1.44870156 C37.1892239 1.47269179 33.25675658 1.49138239 29.32408524 1.49761391 C25.62550831 1.50363876 21.92727054 1.52354315 18.22881889 1.55427551 C16.21821404 1.56731688 14.2075301 1.56547871 12.19688416 1.5632782 C11.00773911 1.57581635 9.81859406 1.58835449 8.59341431 1.60127258 C7.55547541 1.60588155 6.51753651 1.61049051 5.44814491 1.61523914 C4.64025709 1.74221023 3.83236927 1.86918131 3 2 C2.01 3.485 2.01 3.485 1 5 C0.67 3.35 0.34 1.7 0 0 Z "
      fill="#DDDDDD"
      transform="translate(158,212)"
    />
    <path
      d="M0 0 C0.66 0.66 1.32 1.32 2 2 C-0.97 5.3 -3.94 8.6 -7 12 C-7.66 11.01 -8.32 10.02 -9 9 C-8.01 8.34 -7.02 7.68 -6 7 C-4.78715124 3.94570927 -4.78715124 3.94570927 -4 1 C-3.01 1 -2.02 1 -1 1 C-0.67 0.67 -0.34 0.34 0 0 Z "
      fill="#090909"
      transform="translate(126,124)"
    />
    <path
      d="M0 0 C-0.99 1.485 -0.99 1.485 -2 3 C-3.9453125 3.29296875 -3.9453125 3.29296875 -6.125 3.1875 C-7.40375 3.125625 -8.6825 3.06375 -10 3 C-10 2.01 -10 1.02 -10 0 C-2.25 -1.125 -2.25 -1.125 0 0 Z "
      fill="#121212"
      transform="translate(182,75)"
    />
    <path
      d="M0 0 C0.66 0.33 1.32 0.66 2 1 C0.515 2.485 0.515 2.485 -1 4 C-0.67 4.66 -0.34 5.32 0 6 C-2.31 5.67 -4.62 5.34 -7 5 C-2.25 1.125 -2.25 1.125 0 0 Z "
      fill="#181818"
      transform="translate(171,136)"
    />
    <path
      d="M0 0 C4 4.5 4 4.5 4 9 C3.34 8.67 2.68 8.34 2 8 C2 6.68 2 5.36 2 4 C0.68 3.34 -0.64 2.68 -2 2 C-1.34 2 -0.68 2 0 2 C0 1.34 0 0.68 0 0 Z "
      fill="#E6E6E6"
      transform="translate(90,265)"
    />
    <path
      d="M0 0 C-1.39332574 3.36720387 -2.9789286 4.9859524 -6 7 C-6.33 6.01 -6.66 5.02 -7 4 C-2.25 0 -2.25 0 0 0 Z "
      fill="#1A1A1A"
      transform="translate(124,129)"
    />
  </svg>
);

// const Blast = ({ testnet, ...props }: { testnet?: boolean }) => (
//   <svg
//     {...props}
//     aria-hidden="true"
//     width="44"
//     height="44"
//     viewBox="0 0 128 128"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <path
//       d="M0 0 C42.24 0 84.48 0 128 0 C128 42.24 128 84.48 128 128 C85.76 128 43.52 128 0 128 C0 85.76 0 43.52 0 0 Z "
//       fill="#FBFB02"
//       transform="translate(0,0)"
//     />
//     <path
//       d="M0 0 C1.29626816 -0.00432541 2.59253632 -0.00865082 3.92808533 -0.0131073 C6.65588944 -0.01887515 9.38206966 -0.00018386 12.10961914 0.03686523 C15.58233542 0.08239403 19.05191484 0.06972176 22.5246973 0.03905773 C25.87091674 0.01684949 29.21626874 0.04154386 32.5625 0.0625 C34.41440697 0.0489875 34.41440697 0.0489875 36.3037262 0.03520203 C44.24833063 0.18576309 49.15483387 1.67114298 55.21484375 6.89453125 C54.95700697 8.90565812 54.59904373 10.90367679 54.21484375 12.89453125 C54.10011719 13.61769531 53.98539062 14.34085938 53.8671875 15.0859375 C52.92855388 19.19506689 52.06398095 21.28119581 48.5703125 23.8046875 C45.82568204 25.29503241 43.08020729 26.65400884 40.21484375 27.89453125 C40.21484375 28.55453125 40.21484375 29.21453125 40.21484375 29.89453125 C42.17632249 31.28391202 44.17650951 32.62057235 46.21484375 33.89453125 C45.70230165 40.94198511 44.80867926 47.70762156 39.26782227 52.62109375 C28.09200342 59.30025375 15.01841579 57.53817431 2.46484375 57.26953125 C0.01435568 57.23939433 -2.43616809 57.21202513 -4.88671875 57.1875 C-10.85365094 57.12180305 -16.81923525 57.02175013 -22.78515625 56.89453125 C-15.80058377 33.54211776 -15.80058377 33.54211776 -13.03515625 24.70703125 C-12.74511719 23.75634766 -12.45507812 22.80566406 -12.15625 21.82617188 C-11.87394531 20.92705078 -11.59164062 20.02792969 -11.30078125 19.1015625 C-11.05618164 18.31241455 -10.81158203 17.5232666 -10.55957031 16.71020508 C-9.78515625 14.89453125 -9.78515625 14.89453125 -7.78515625 13.89453125 C-5.14515625 15.87453125 -2.50515625 17.85453125 0.21484375 19.89453125 C-1.81250285 28.89397227 -4.65412522 37.12764436 -7.78515625 45.89453125 C5.41484375 45.89453125 18.61484375 45.89453125 32.21484375 45.89453125 C33.69984375 40.44953125 33.69984375 40.44953125 35.21484375 34.89453125 C35.54484375 33.90453125 35.87484375 32.91453125 36.21484375 31.89453125 C26.97484375 31.89453125 17.73484375 31.89453125 8.21484375 31.89453125 C8.87484375 29.25453125 9.53484375 26.61453125 10.21484375 23.89453125 C19.78484375 23.89453125 29.35484375 23.89453125 39.21484375 23.89453125 C40.20484375 20.26453125 41.19484375 16.63453125 42.21484375 12.89453125 C19.11484375 12.56453125 -3.98515625 12.23453125 -27.78515625 11.89453125 C-18.85278804 2.96216304 -12.73867052 -0.17886377 0 0 Z "
//       fill="#000000"
//       transform="translate(49.78515625,36.10546875)"
//     />
//   </svg>
// );

// const Mantle = ({ testnet, ...props }: { testnet?: boolean }) => (
//   <svg
//     {...props}
//     aria-hidden="true"
//     width="44"
//     height="44"
//     viewBox="0 0 311 311"
//     xmlns="http://www.w3.org/2000/svg"
//   >
//     <style>{".st0{fill:#fff}"}</style>
//     <path d="M0 0h314v311H0z" />
//     <path
//       d="M155.5 289.1v-45.7c-4.6 0-9.3-.4-13.9-1.1l-7.2 45.1c7 1.2 14.1 1.7 21.1 1.7zm21-1.6c7-1.1 13.9-2.7 20.6-4.9L183 239.1c-4.4 1.4-9 2.5-13.6 3.2l7.1 45.2zm-62.6-4.9 14.1-43.4c-4.4-1.4-8.7-3.2-12.9-5.3l-20.8 40.7c6.3 3.1 12.9 5.8 19.6 8zm102.7-8c6.3-3.2 12.3-6.9 18-11l-27-36.9c-3.7 2.7-7.7 5.2-11.9 7.2l20.9 40.7zM76.3 263.5l27-36.9c-3.7-2.7-7.3-5.7-10.6-9l-32.4 32.3c5 4.9 10.4 9.5 16 13.6zm174.2-13.4.3-.3c5-4.9 9.6-10.3 13.7-15.9l-37-26.9c-2.7 3.7-5.8 7.3-9 10.5l-.5.5 15.6 14.7 16.9 17.4zM46.6 233.8l37-26.9c-2.7-3.7-5.2-7.7-7.3-11.8l-40.8 20.7c3.2 6.3 6.9 12.3 11.1 18zm228.9-17.9c3.2-6.2 5.9-12.8 8.1-19.5l-43.6-14c-1.4 4.4-3.2 8.7-5.3 12.8l40.8 20.7zM27.4 196.4 71 182.3c-1.4-4.4-2.5-8.9-3.2-13.5l-45.3 7.1c1.1 6.9 2.8 13.8 4.9 20.5zM288.5 176c1.1-6.9 1.7-14 1.7-21h-45.8c0 4.6-.4 9.3-1.1 13.9l45.2 7.1zM66.7 154.9c0-4.6.4-9.3 1.1-13.9l-45.3-7.1c-1.1 6.9-1.6 14-1.7 21h45.9zm176.6-13.8 45.3-7.1c-1.1-6.9-2.7-13.8-4.9-20.5l-43.6 14c1.4 4.5 2.5 9 3.2 13.6zM71 127.5c1.4-4.4 3.2-8.7 5.3-12.8L35.5 94c-3.2 6.2-5.9 12.8-8.1 19.5l43.6 14zm163.8-12.7 40.9-20.7c-3.2-6.3-6.9-12.3-11.1-18L227.5 103c2.7 3.7 5.2 7.7 7.3 11.8zM83.6 102.9c2.7-3.7 5.8-7.3 9.1-10.6l.1-.1-32.4-32.3-.1.1c-5 5-9.6 10.3-13.7 16l37 26.9zm134.9-10.4 9.8-9.9 22.5-22.4-.1-.1c-5-4.9-10.3-9.5-15.9-13.6l-27 36.9c3.7 2.7 7.3 5.7 10.5 9l.2.1zm-115.1-9.3c3.7-2.7 7.7-5.1 11.9-7.2L94.5 35.3c-6.3 3.2-12.3 6.9-18 11l26.9 36.9zm92.6-7.1 20.9-40.7c-6.3-3.2-12.8-5.9-19.5-8.1l-14.2 43.4c4.3 1.5 8.6 3.3 12.8 5.4zm-67.8-5.4c4.4-1.4 9-2.5 13.6-3.2l-7.1-45.1c-6.9 1.1-13.9 2.7-20.6 4.9l14.1 43.4zm41.3-3.2 7.2-45.1c-6.9-1.1-14-1.7-21-1.7v45.7c4.6 0 9.3.4 13.8 1.1z"
//       className="st0"
//     />
//     <path
//       d="M155.5 245.5v-45.9c-3.9 0-7.7-.5-11.5-1.5l-12 44.3c7.7 2 15.6 3.1 23.5 3.1zm23.5-3.1c7.7-2 15-5 21.9-9l-23.2-39.7c-3.3 1.9-6.9 3.4-10.6 4.4l11.9 44.3zm-68.8-9 23.2-39.7c-3.3-1.9-6.4-4.3-9.2-7l-.4-.4-14.6 15.3-18 17.3.3.3c5.6 5.5 11.9 10.3 18.7 14.2zm109.4-14.2c5.6-5.6 10.5-11.8 14.4-18.6l-40.1-23c-1.9 3.3-4.3 6.3-7.1 9.1l32.8 32.5zM77 200.5l40.1-22.9c-1.9-3.3-3.4-6.8-4.4-10.5L68 178.9c2 7.5 5.1 14.8 9 21.6zM243.1 179c2-7.6 3.1-15.4 3.1-23.2h-46.3c0 3.8-.5 7.7-1.5 11.4l44.7 11.8zm-131.8-23.3c0-3.8.5-7.7 1.5-11.4L68 132.5c-2 7.6-3.1 15.4-3.1 23.2h46.4zm87-11.3 44.8-11.8c-2-7.6-5.1-14.9-9-21.7L194 133.8c1.8 3.4 3.3 6.9 4.3 10.6zm-81.1-10.6c1.9-3.2 4.2-6.3 7-9l.1-.1-16.6-16.4L91.8 92l-.3.3c-5.6 5.5-10.4 11.8-14.4 18.5l40.1 23zm-9.5-25.5.2.2-.2-.2zm79.1 16.5 16.4-16.3L220 92.7l-.4-.4C214 86.7 207.7 82 200.9 78l-23.2 39.7c3.3 1.9 6.3 4.2 9.1 6.9v.2zm-53.4-7.1c3.3-1.9 6.9-3.4 10.6-4.4L132.2 69c-7.7 2-15 5-21.9 9l23.1 39.7zm33.6-4.3 12-44.3c-7.6-2-15.5-3.1-23.4-3.1v45.9c3.8 0 7.7.5 11.4 1.5z"
//       className="st0"
//     />
//   </svg>
// );

const Scroll = ({ ...props }) => (
  <svg
    {...props}
    width="44"
    height="44"
    viewBox="0 0 444 444"
    xmlns="http://www.w3.org/2000/svg"
  >
    <style>{".st0{fill:#ffeeda}.st2{fill:#101010}"}</style>
    <path d="M0 0h444v444H0z" className="st0" />
    <path
      d="M333.4 262V110.6c-.2-12.7-10.3-22.9-23-22.9h-158c-34.1.5-61.5 28.4-61.5 62.6 0 11.5 3.1 21.4 7.8 30.1 4 7.2 10.3 14 16.5 19.1 1.8 1.4.9.8 6.3 4.1 7.4 4.5 15.9 6.8 15.9 6.8l-.1 90.6c.2 4.3.6 8.4 1.8 12.3 3.5 12.8 12.4 22.6 24.4 27.3 5 2 10.7 3.3 16.8 3.4l126.2.4c25.1 0 45.5-20.4 45.5-45.6.1-15-7.4-28.4-18.6-36.8z"
      className="st0"
    />
    <path
      d="M336.5 299.9C336 316 322.7 329 306.4 329l-86.8-.3c6.9-8 11.1-18.4 11.1-29.8 0-17.8-10.6-30.1-10.6-30.1h86.4c16.6 0 30.1 13.5 30.1 30.1l-.1 1z"
      style={{
        fill: "#ebc28e",
      }}
    />
    <path
      d="M123.5 186.7c-10-9.5-17-21.7-17-36.3v-1.5c.8-24.8 21.2-44.8 46-45.5h158c4.1.2 7.4 3.1 7.4 7.3v133.7c3.6.6 5.4 1.1 8.9 2.4 2.8 1 6.6 3.2 6.6 3.2V110.7c-.2-12.7-10.3-22.9-23-22.9h-158c-34.1.5-61.5 28.4-61.5 62.6 0 19.9 9.1 36.9 23.9 48.8 1 .8 2 1.9 4.6 1.9 4.6 0 7.9-3.7 7.7-7.7 0-3.4-1.5-4.6-3.6-6.7z"
      className="st2"
    />
    <path
      d="M306.4 253.2H182.5c-8.3.1-15 6.8-15 15.1v17.8c.2 8.2 7.2 15.2 15.6 15.2h9.2v-15.2H183v-17.4h5c15.7 0 27.2 14.5 27.2 30.1 0 13.8-12.6 31.4-33.6 30-18.6-1.2-28.7-17.8-28.7-30v-151c0-6.8-5.6-12.4-12.4-12.4h-12.4v15.5h9.2v148c-.5 30.1 21.4 45.2 44.3 45.2l124.9.4c25.1 0 45.5-20.4 45.5-45.6.1-25.2-20.4-45.7-45.6-45.7zm30.1 46.7C336 316 322.7 329 306.4 329l-86.8-.3c6.9-8 11.1-18.4 11.1-29.8 0-17.8-10.6-30.1-10.6-30.1h86.4c16.6 0 30.1 13.5 30.1 30.1l-.1 1zM273 153.7h-93.4v-15.5H273c4.2 0 7.7 3.4 7.7 7.7.1 4.3-3.3 7.8-7.7 7.8z"
      className="st2"
    />
    <path
      d="M273 226.4h-93.4V211H273c4.2 0 7.7 3.4 7.7 7.7.1 4.2-3.3 7.7-7.7 7.7zM289.5 190H179.6v-15.5h109.8c4.2 0 7.7 3.4 7.7 7.7.2 4.3-3.3 7.8-7.6 7.8z"
      className="st2"
    />
  </svg>
);

export const chainToLogo = {
  [arbitrum.chainId]: <Arbitrum />,
  [base.chainId]: <Base />,
  [bsc.chainId]: <BinanceSmartChain />,
  [celo.chainId]: <Celo />,
  [ethereum.chainId]: <Ethereum />,
  [linea.chainId]: <Linea />,
  [optimism.chainId]: <Optimism />,
  [polygon.chainId]: <Polygon />,
  [scroll.chainId]: <Scroll />,
  [solana.chainId]: <Solana />,
  [worldchain.chainId]: <Worldchain />,
};
